/**
 * Task domain helpers used by `TasksIndex`.
 *
 * `TaskCellValue` is the canonical shape (see `dtype.ts`). `TaskCalendarItem`
 * wraps a task with a computed `DateTime.Range<Utc>` derived from
 * its scheduled start/end or due date — what calendar renderers
 * consume when they need to lay tasks out on a timeline.
 */

import { DateTime, Duration, MsSinceEpoch, type Utc } from "@tento-chrono";

import type { TaskCellValue } from "../core/dtype.ts";
import type { TaskId } from "../ids/ids.ts";

/** Calendar range of a canonical task; valid start-only tasks remain unscheduled. */
export function taskCalendarRange(task: TaskCellValue): DateTime.Range<Utc> {
  if (task.scheduledStart && task.scheduledEnd) return new DateTime.Range(task.scheduledStart, task.scheduledEnd);
  if (task.dueDate) return new DateTime.Range(task.dueDate, task.dueDate.add(Duration.Time.MIN15));
  const epoch = DateTime.fromMse(MsSinceEpoch.of(0), DateTime.utc);
  return new DateTime.Range(epoch, epoch.add(Duration.Time.MIN15));
}

/** The tasks index's existing strict-overlap policy, intentionally unchanged. */
export function taskCalendarBelongs(span: DateTime.Range<Utc>, range: DateTime.Range<Utc>): boolean {
  return span.start.mse < range.end.mse && span.end.mse > range.start.mse;
}

/**
 * A task as calendar renderers lay it out: the `TaskCellValue` with its
 * resolved time range (`taskCalendarRange`):
 *   - `scheduledStart` + `scheduledEnd` → `[start, end]`;
 *   - otherwise `dueDate`               → `[dueDate, dueDate + 15m]`;
 *   - neither → `[epoch, epoch + 15m]`, no calendar placement: an
 *     unscheduled task gets no item (`peekInRange` never surfaces it); a
 *     valid start-only task keeps an item at the epoch sentinel, outside
 *     any real range. Both still appear in `peekAll()`.
 */
export class TaskCalendarItem {
  readonly id: TaskId;
  readonly time: DateTime.Range<Utc>;
  readonly task: TaskCellValue;

  constructor(task: TaskCellValue) {
    this.id = task.id;
    this.task = task;

    this.time = taskCalendarRange(task);
  }

  get title(): string {
    return this.task.title || "Untitled task";
  }

  get isCompleted(): boolean {
    return this.task.status === "done" || this.task.completedAt != null;
  }

  get isScheduled(): boolean {
    return this.task.scheduledStart != null && this.task.scheduledEnd != null;
  }
}
